import { isServerRendering } from '@farmasiet/frontendcore-reactcomponents';

import { IProductTrackingData } from '../../../Catalog/Product/IProductTrackingData.csharp';
import { ProductViewModel } from '../../../Catalog/Product/ViewModels/ProductViewModel.csharp';
import { SearchResultProductViewModel } from '../../../Filtering/Api/SearchResultProductViewModel.csharp';
import { ProductCardViewModel } from '../../../Partials/ProductCard/ProductCardViewModel.csharp';
import { CartItemViewModel } from '../../../Purchase/Cart/Item/ViewModels/CartItemViewModel.csharp';
import { GoogleTagManagerDataLayer } from '../GoogleTagManagerDataLayer';
import { GoogleTagManagerList } from '../GoogleTagManagerList';
import { GTMClickType } from '../GTMClickType';
import { IGoogleTagManagerAnalyticsTracker } from '../IGoogleTagManagerAnalyticsTracker';
import { ITrackingData } from '../ITrackingData';
import { PromotionImpressionViewModel } from '../PromotionImpression/PromotionImpressionViewModel.csharp';
import { SearchVariant } from '../SearchVariant';
import { GoogleAnalytics4Actions } from './GoogleAnalytics4Actions';
import { GA4Object } from './GoogleAnalytics4Interfaces';
import { GoogleAnalytics4Mapper } from './GoogleAnalytics4Mapper';

declare global {
    interface Window {
        enhancedConversionsUserData: {
            userId: string;
            email: string;
        };
    }
}

export class GoogleAnalytics4Tracker implements IGoogleTagManagerAnalyticsTracker {
    private readonly _googleTagManagerDataLayer = new GoogleTagManagerDataLayer();
    private readonly _mapper = new GoogleAnalytics4Mapper();
    private readonly _currency = 'NOK';

    constructor() {
        // We need to push user data to data layer first, so it is available when other events like 'purchase' are pushed.
        // That's why we do this in the constructor.
        if (!isServerRendering() && window.enhancedConversionsUserData) {
            // grab user data attached to window during rendering of EnhancedConversionsUserData.cshtml
            const { userId, email } = window.enhancedConversionsUserData;
            this._googleTagManagerDataLayer.addUserData(userId, email);
        }
    }

    trackPurchase = (products: CartItemViewModel[], data: ITrackingData) => {
        const items = products.map((x) => this._mapper.mapCartItemViewModelToItemData(x));

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.purchase,
            ecommerce: {
                transaction_id: data.id,
                value: data.revenue,
                tax: data.tax,
                shipping: data.shipping,
                shipping_tier: data.shippingMethod,
                coupon: data.coupon,
                currency: this._currency,
                items: items
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    };

    trackItemView = (product: ProductViewModel, originName: string, promotionId?: string, promotionName?: string) => {
        const item = this._mapper.mapProductToItemData(product, originName, promotionId, promotionName);

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.viewItem,
            ecommerce: {
                currency: this._currency,
                items: [item]
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    };

    trackProductCardItemSelect = (product: ProductCardViewModel, productLink: string, clickType: GTMClickType) => {
        const item = this._mapper.mapProductCardToItemData(product);

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.selectItem,
            ecommerce: {
                item_list_name: product.originName ?? GoogleTagManagerList.productCard,
                items: [item]
            }
        };

        this._googleTagManagerDataLayer.pushWithRedirect(obj, productLink, clickType);
    };

    trackCartItemItemSelect = (product: CartItemViewModel, productLink: string, clickType: GTMClickType) => {
        const item = this._mapper.mapCartItemViewModelToItemData(product);

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.selectItem,
            ecommerce: {
                item_list_name: GoogleTagManagerList.cartItems,
                items: [item]
            }
        };

        this._googleTagManagerDataLayer.pushWithRedirect(obj, productLink, clickType);
    };

    trackSearchItemSelect = (product: SearchResultProductViewModel, productLink: string, clickType: GTMClickType) => {
        const item = this._mapper.mapSearchItemToItemData(product);

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.selectItem,
            ecommerce: {
                item_list_name: GoogleTagManagerList.searchResults,
                items: [item]
            }
        };

        this._googleTagManagerDataLayer.pushWithRedirect(obj, productLink, clickType);
    };

    trackProductCardsItemImpressions = (products: ProductCardViewModel[]) => {
        const items = products.map((x) => this._mapper.mapProductCardToItemData(x));
        const product = products[0];

        if (!product) return;

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.viewItemList,
            ecommerce: {
                item_list_name: product.originName ?? GoogleTagManagerList.productCard,
                items: items
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    };

    trackSearchResultsItemImpressions = (products: SearchResultProductViewModel[]) => {
        const items = products.map((x) => this._mapper.mapSearchItemToItemData(x));

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.viewItemList,
            ecommerce: {
                items: items
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    };

    trackAddToCart = (product: IProductTrackingData, quantity: number, id?: string, originName?: string, promotionId?: string, promotionName?: string) => {
        const item = this._mapper.mapProductTrackingDataToItemData(product, quantity, originName, promotionId, promotionName);

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.addToCart,
            ecommerce: {
                currency: this._currency,
                value: product.placedPrice * quantity,
                items: [item]
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    };

    trackRemoveFromCart = (product: CartItemViewModel, quantity: number) => {
        const item = this._mapper.mapCartItemViewModelToItemData(product, quantity);

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.removeFromCart,
            ecommerce: {
                currency: this._currency,
                value: product.discountedPrice,
                items: [item]
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    };

    trackAddToWishList = (product: IProductTrackingData) => {
        const item = this._mapper.mapProductTrackingDataToItemData(product);

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.addToWishList,
            ecommerce: {
                value: product.placedPrice,
                currency: this._currency,
                items: [item]
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    };

    trackBeginCheckout = (products: CartItemViewModel[], data: ITrackingData) => {
        const items = products.map((x, idx) => this._mapper.mapCartItemViewModelToItemDataExtended(x, idx, data.coupon!));

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.beginCheckout,
            ecommerce: {
                currency: this._currency,
                value: data.revenue,
                coupon: data.coupon,
                items: items
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    };

    trackAddShippingInfo = (products: CartItemViewModel[], data: ITrackingData) => {
        const items = products.map((x, idx) => this._mapper.mapCartItemViewModelToItemDataExtended(x, idx, data.coupon!));

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.addShippingInfo,
            ecommerce: {
                currency: this._currency,
                value: data.revenue,
                coupon: data.coupon,
                shipping_tier: data.shippingMethod,
                items: items
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    };

    trackAddPaymentInfo = (products: CartItemViewModel[], data: ITrackingData) => {
        const items = products.map((x) => this._mapper.mapCartItemViewModelToItemData(x));

        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.addPaymentInfo,
            ecommerce: {
                currency: this._currency,
                value: data.revenue,
                coupon: data.coupon,
                payment_type: data.paymentMethod,
                items: items
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    };

    trackPromotionsView = (promotions: PromotionImpressionViewModel[]) => {
        promotions.map((promotion) => {
            const obj: GA4Object = {
                event: GoogleAnalytics4Actions.viewPromotion,
                ecommerce: {
                    creative_name: promotion.creative,
                    creative_slot: promotion.position,
                    promotion_id: promotion.id,
                    promotion_name: promotion.name
                }
            };

            this._googleTagManagerDataLayer.push(obj);
        });
    };

    trackPromotionSelect = (promotion: PromotionImpressionViewModel, promotionLink: string, clickType: GTMClickType) => {
        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.selectPromotion,
            ecommerce: {
                creative_name: promotion.creative,
                creative_slot: promotion.position,
                promotion_id: promotion.id,
                promotion_name: promotion.name
            }
        };

        this._googleTagManagerDataLayer.pushWithRedirect(obj, promotionLink, clickType);
    };

    trackViewCart = (items: CartItemViewModel[], total: number, couponCode: string, isMiniCart?: boolean) => {
        const obj: GA4Object = {
            event: isMiniCart ? GoogleAnalytics4Actions.viewMiniCart : GoogleAnalytics4Actions.viewCart,
            ecommerce: {
                currency: this._currency,
                value: total,
                items: items.map((item, idx) => this._mapper.mapCartItemViewModelToItemDataExtended(item, idx, couponCode))
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    };

    trackSearchSuggestions(searchTerm: string): void {
        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.searchSuggestions,
            ecommerce: {
                search_term: searchTerm
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    }

    trackSearchResultsView(searchTerm: string, variant: SearchVariant): void {
        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.viewSearchResults,
            ecommerce: {
                search_term: searchTerm,
                search_variant: variant
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    }

    trackSearchTagClick(tagValue: string, tagIndex: number): void {
        const obj: GA4Object = {
            event: GoogleAnalytics4Actions.applySearchTagClick,
            ecommerce: {
                tag_value: tagValue,
                tag_index: tagIndex
            }
        };

        this._googleTagManagerDataLayer.push(obj);
    }
}
